/**
 * Reader questions → "Contact page address" (Prototype v15): what the "Ask the
 * team" button will do with the address as typed. Empty, not a path on the
 * site, no page there, or a page that isn't live yet all mean readers get no
 * button (QA round 7: the button was hidden and nothing said so).
 */

export interface ContactPage {
  path: string;
  title?: string | null;
  state?: { live?: unknown } | null;
}
export interface ContactForm {
  name?: string | null;
  placedOn?: Array<{ path: string }> | null;
}
export interface ContactNote {
  text: string;
  tone: 'ok' | 'warn' | 'bad';
  /** Readers get the "Ask the team" button with this address. */
  teamOk: boolean;
}

const norm = (p: string) => p.replace(/\/+$/, '') || '/';

/** Addresses that are always there: the home page and the built-in lists. */
const BUILT_IN: Record<string, string> = {
  '/': 'your home page',
  '/articles': 'your articles',
  '/ask': 'your Ask page',
};

export function contactNote(
  typed: string,
  pages: ContactPage[] | null,
  forms: ContactForm[] = []
): ContactNote {
  const value = typed.trim();
  if (!value) {
    return {
      text: 'No address, so the “Ask the team” button isn’t shown.',
      tone: 'warn',
      teamOk: false,
    };
  }
  if (!value.startsWith('/') || value.startsWith('//')) {
    return { text: 'Use an address on your site, starting with /.', tone: 'bad', teamOk: false };
  }
  const path = norm(value.split(/[?#]/)[0]);
  if (BUILT_IN[path]) {
    return { text: `Opens ${BUILT_IN[path]}.`, tone: 'ok', teamOk: true };
  }
  // Pages not loaded yet: say what the button does, without a verdict.
  if (!pages) {
    return {
      text: 'A page on your site. Without an address, the button isn’t shown.',
      tone: 'ok',
      teamOk: true,
    };
  }
  const page = pages.find((p) => norm(p.path || '') === path);
  if (!page) {
    return {
      text: `There’s no page at ${path}. Readers would land on “Page not found”.`,
      tone: 'warn',
      teamOk: false,
    };
  }
  if (!page.state?.live) {
    return {
      text: 'That page is still a draft. Publish it first, or pick another.',
      tone: 'warn',
      teamOk: false,
    };
  }
  const form = forms.find((f) => (f.placedOn || []).some((p) => norm(p.path) === path));
  const title = page.title?.trim() || path;
  return {
    text: `Opens ${title}${form?.name ? `, which has your ${form.name} form` : ''}. Without an address, the button isn’t shown.`,
    tone: 'ok',
    teamOk: true,
  };
}
